/**
 * ScrollToTop / ScrollRestoration
 *
 * On PUSH/REPLACE navigation (user clicks a link): jump to the top of
 * the viewport — new page starts fresh.
 *
 * On POP navigation (user hits back/forward, either the browser's own
 * button or an in-app `navigate(-1)`): restore the scroll position the
 * user had on that page the last time they visited it. Anna's feedback:
 * "wenn man dann zurück gehen will … dann kommt man nicht auf die
 * Skulptur, die man sich gerade angesehen hat oder in dieser Höhe" —
 * she wants to land back at the sculpture she opened, not at the top
 * (or, worse, wherever the browser's native scrollRestoration decides).
 *
 * We disable native scrollRestoration entirely so this hook is the
 * single source of truth — no race with the browser.
 *
 * Renders nothing.
 */

import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

// Module-scope so the cache survives StrictMode's double-mount during
// a route change; a component-local Map would lose the entry.
const scrollPositions = new Map<string, number>()

export function ScrollToTop() {
  const { pathname } = useLocation()
  const navType = useNavigationType()

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  useEffect(() => {
    if (navType === 'POP') {
      const y = scrollPositions.get(pathname) ?? 0
      window.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior })
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
    }
  }, [pathname, navType])

  useEffect(() => {
    const handler = () => {
      scrollPositions.set(pathname, window.scrollY)
    }
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [pathname])

  return null
}
